(ns manatee.admin.queue.components
  "The components that comprise the contents of the admin. queues tab."
  (:require
   [clojure.set :as set]
   [clojure.string :as string]
   [manatee.admin.components :as admin-components]
   [manatee.admin.queue.handlers :as queue-handlers]
   [manatee.admin.queue.subs :as queue-subs]
   [manatee.admin.subs :as admin-subs]
   [manatee.admin.task-config.subs :as task-config-subs]
   [manatee.components.modal.handlers :as modal-handlers]
   [manatee.components.modal.subs :as modal-subs]
   [manatee.ui.subs :as ui-subs]
   [manatee.user.subs :as user-subs]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [rothko.accordion.component :as accordion-component]
   [rothko.button.component :as button-component]
   [rothko.collection-components.core :as collection]
   [rothko.collection-components.table.component :as table-component]
   [rothko.form2.checkbox.component :as checkbox]
   [rothko.form2.component :as form]
   [rothko.form2.input.component :as input]
   [rothko.form2.radio.component :as radio]
   [rothko.form2.select.core :as select]
   [rothko.form2.select.multi-select.component :as multi-select-component]
   [rothko.form2.validation :as validation]
   [rothko.form2.validation-message.component :as validation-message]
   [rothko.panel.component :as panel-component]
   [rothko.tooltip.component :as tooltip-component]))

(defn format-task-name
  "Format task name for presentation to user."
  [account-id task-name]
  (let [{:keys [account-ids]} @(rf/subscribe [::ui-subs/config])
        account-name          (when account-id
                                (some->> account-ids
                                         (filter #(= (second %) account-id))
                                         (first)
                                         (first)
                                         (name)
                                         (string/capitalize)))]
    (some-> (cond->> task-name
              account-name (str account-name ": "))
            (string/replace #" est" " EST")
            (string/replace #"-" " ")
            (string/replace #" aaet" " AAET")
            (string/replace #" api" " API"))))

(defn- format-queue-cells
  [{:keys [is-archived? task-config-id users team-id] :as queue-config}]
  (let [task-configs @(rf/subscribe [::task-config-subs/task-configs-map])
        user-teams @(rf/subscribe [::admin-subs/user-teams])
        task-config  (get task-configs (str task-config-id))]
    (assoc queue-config
           :task-name (format-task-name (get-in task-config [:data :account-id])
                                        (:task-name task-config))
           :team (:representation (first (filter #(= (:value %)
                                                     team-id)
                                                 user-teams)))
           :users (->> users
                       (map (fn [{:keys [value email]}]
                              (tooltip-component/inline-tooltip value email)))
                       (interpose ", ")
                       (into [:div]))
           :is-archived? (if is-archived?
                           "✓"
                           "✗"))))

(defn- format-user-name
  [{:keys [first-name last-name]}]
  (str first-name " " last-name))

(defn- assigned-user-and-queue-names
  "The names and assigned queue names of all queue users that are assigned to
   another queue."
  [assigned-users queue-users]
  (for [user-id queue-users
        :when   (contains? assigned-users user-id)]
    (get assigned-users user-id)))

(defn- assigned-users-user-and-queue-names
  "The identifiers of all users assigned to queues (except the selected queue)
   mapped to a map of their and their assigned queue names."
  [id queue-configs active-users]
  (into {}
        (for [config queue-configs
              :when  (not= id
                           (:id config))
              user   (:users config)]
          [(:id user) {:user-name  (format-user-name (first (filter #(= (:id user)
                                                                        (:id %))
                                                                    active-users)))
                       :queue-name (:name config)}])))

(defn- queue-form-submission-reassigned-users-msg
  "The message (and supporting information) displayed when requesting
   confirmation of adding or updating a queue configuration that would result
   in queue users being assigned from another queue."
  [assigned-users queue-users]
  [:<>
   [:span "In completing this action, the following users will be assigned from their current queues. Are you sure you want to add/update this queue configuration?"]
   [table-component/component
    {:appearance        #{:primary}
     ::collection/items  (sort-by :queue-name
                                  (assigned-user-and-queue-names assigned-users queue-users))
     ::collection/fields [{::collection/id    :user-name
                           ::collection/label "Name"}
                          {::collection/id    :queue-name
                           ::collection/label "Queue"}]}]])

(defn- queue-form-submission-msg
  "The message displayed when requesting confirmation of adding or updating a
   queue configuration that would not result in queue users being assigned from
   another queue."
  []
  [:<>
   [:span "Are you sure you want to add/update this queue configuration?"]])

(defn- select-queue-panel-view
  "Switch to the list view if LIST-VIEW? is true, the form view (to view the
   configuration identified by ID, if not nil) otherwise."
  [id list-view?]
  (rf/dispatch [::queue-handlers/select-queue-config id])
  (rf/dispatch [::queue-handlers/set-list-view list-view?]))

(defn- upsert-confirmation-dialog
  "Ask for confirmation of queue configuration addition/updating."
  [id active-users]
  (let [{:keys [queue-users] :as event}  @(rf/subscribe [::modal-subs/modal-props])
        queue-configs                    @(rf/subscribe [::queue-subs/queue-configurations])
        assigned-users                   (assigned-users-user-and-queue-names id queue-configs active-users)
        msg                              (if (some (set queue-users)
                                                   (keys assigned-users))
                                           (queue-form-submission-reassigned-users-msg assigned-users queue-users)
                                           (queue-form-submission-msg))]
    [admin-components/confirmation-dialog
     {:id        :confirm-upsert-queue-config
      :class     [:admin-page__confirmation-dialog]
      :ok-fn     #(do (rf/dispatch [::queue-handlers/upsert-queue-config event])
                      (rf/dispatch [::modal-handlers/hide :confirm-upsert-queue-config])
                      (select-queue-panel-view nil true))
      :cancel-fn #(rf/dispatch [::modal-handlers/hide :confirm-upsert-queue-config])}
     msg]))

(defn- queue-name-field-props
  [{:keys [name]
    :as   _selected-config}]
  {:appearance          #{:primary}
   :class               [:admin-page__input]
   ::form/label          "Name"
   ::form/initial-value  name
   ::input/type          :text})

(defn- queue-users-field-props
  [{:keys [users]
    :as   _selected-config} active-users]
  {::form/label          "Users"
   ::form/placeholder    "Add users..."
   ::form/initial-value  (set users)
   ::select/options      (for [{:keys [id email]
                                :as   user} active-users]
                           {:value          id
                            :representation (format-user-name user)
                            :tooltip        email})
   :class               [:admin-page__queue-user-selector]
   :appearance          #{:primary :pills-inside-input}})

(defn- task-config-id-field-props
  [{:keys [task-config-id]
    :as   _selected-config} task-options]
  {::form/initial-value  (str task-config-id)
   ::radio/options       (sort-by :label task-options)
   :appearance          #{:right-label}})

(def ^:private queue-form-validation-messages
  {:name-not-empty          "Name must not be empty"
   :task-not-selected       "Task must be selected"
   :users-empty-if-archived "Must have no users if archived"})

(defn- queue-form-validation-fn
  "Validation function for the queue configuration form as a whole."
  [{:keys [is-archived? queue-name queue-users task-config-field]
    :as   _form-state}]
  (cond
    (string/blank? task-config-field) [:task-not-selected]
    (string/blank? queue-name) [:name-not-empty]
    (and is-archived?
         (not-empty queue-users)) [:users-empty-if-archived]
    :else ::validation/success))

(defn- queue-form-view
  "The view containing the add/edit/delete queue configuration form."
  [selected-config]
  (let [{:keys [id is-archived? task-config-id]} selected-config
        selected-config (when selected-config
                          (update selected-config :users #(map :id %)))
        title           (if selected-config "Edit configuration" "Add configuration")
        active-users    @(rf/subscribe [::admin-subs/active-users])
        task-configs    @(rf/subscribe [::task-config-subs/active-task-configs])
        task-options    (reduce (fn [result task-config]
                                  (let [account-id (get-in task-config [:data :account-id])
                                        task-name  (:task-name task-config)]
                                    (conj result {:value (:id task-config)
                                                  :label (format-task-name account-id task-name)})))
                                []
                                task-configs)
        task-label      (when selected-config
                          (->> task-options
                               (filter #(= (:value %) (str task-config-id)))
                               (first)
                               :label))]
    [:div {:appearance #{:column}
           :class      [:admin-page__form-layout]}
     [:h4 {:class [:admin-page__header]} title]
     [:div {:class [:admin-page__forms]}
      [form/form
       {::form/name           :queue-form
        ::validation/fn       queue-form-validation-fn
        ::validation/messages queue-form-validation-messages
        ::form/on-submit      #(rf/dispatch [::modal-handlers/show :confirm-upsert-queue-config
                                             (assoc % :task-config-id (:task-config-field %))])}
       {:queue-name     (queue-name-field-props selected-config)
        :is-archived?   {::form/name          :is-archived?
                         ::form/initial-value is-archived?
                         ::checkbox/option    true
                         ::checkbox/label     "Archived?"}
        :queue-users    (queue-users-field-props selected-config active-users)
        :task-config-field (task-config-id-field-props selected-config task-options)}
       (fn [{:keys [::form/submit! ::validation/validate!]
             :as   form-props}
            {:keys [queue-name is-archived? queue-users task-config-field]
             :as   _field-props}]
         (let [validated? (validate! form-props)]
           [:div
            [:form {:class [:admin-page__queue-form]}
             [:div.form-row.button-group
              [button-component/button {:appearance #{:tertiary}
                                        :type       :button
                                        :on-click   #(select-queue-panel-view nil true)}
               "Back"]]
             [input/field queue-name]
             (when selected-config
               [checkbox/field is-archived?])
             (if selected-config
               [:div {:style {:margin-block "1em"}}
                [:label.form__label.primary "Task"]
                [:div (str task-label)]]
               [accordion-component/accordion {:appearance #{:primary}
                                               :open       #{:task-config-id}
                                               :icon-size  22
                                               :items      [{:key     :task-config-id
                                                             :title   [:span {:class [:form__label :primary]}
                                                                       "Tasks"]
                                                             :content [radio/field task-config-field]}]}])
             (when (not= ::validation/success
                         (::validation/form-messages validated?))
               [validation-message/component (set/rename-keys validated?
                                                              {::validation/form-messages ::validation/current-messages})])
             [multi-select-component/field queue-users]
             [:div.form-row.button-group
              [button-component/button {:appearance #{:primary}
                                        :type       :button
                                        :class      [:admin-page__submit-button]
                                        :disabled?  (not= ::validation/success
                                                          (::validation/form-messages validated?))
                                        :on-click   submit!}
               (if selected-config
                 "Update config"
                 "Add config")]]
             [upsert-confirmation-dialog id active-users]]]))]]]))

(defn- queue-list-view
  "The view containing the list of existing queue configurations."
  []
  (let [show-archived? (r/atom false)
        ordering (r/atom {:field :name :order :asc})]
    (fn []
      (let [project-owner? @(rf/subscribe [::user-subs/project-owner?])
            all-queue-configs @(rf/subscribe [::queue-subs/queue-configurations])
            active-queue-configs (filter #(not (:is-archived? %))
                                         all-queue-configs)
            visible-queue-configs (if @show-archived?
                                    all-queue-configs
                                    active-queue-configs)
            sorted-queue-configs (if-let [{:keys [field order]} @ordering]
                                   (sort-by field
                                            (if (= order :asc)
                                              <
                                              >)
                                            visible-queue-configs)
                                   visible-queue-configs)
            all-fields (cond-> [{::collection/id :name
                                 ::collection/label "Name"
                                 ::table-component/ordered? true}
                                {::collection/id :task-name
                                 ::collection/label "Task"
                                 ::table-component/ordered? true}]
                         (not project-owner?) (conj {::collection/id :team
                                                     ::collection/label "Team"})
                         :always (concat [{::collection/id :users
                                           ::collection/label "Users"}
                                          {::collection/id :is-archived?
                                           ::collection/label "Archived?"}]))
            active-fields (butlast all-fields)]
        [:div {:appearance #{:column}
               :class      [:admin-page__table-layout]}
         [:h4 {:class [:admin-page__header]} "Queue configurations"]
         (when-not project-owner?
           [:div.form-row.button-group
            [button-component/button {:appearance #{:tertiary}
                                      :type :button
                                      :on-click #(select-queue-panel-view nil false)}
             "New"]])
         [:div.checkbox__container
          {:class [:manatee]}
          [:input
           {:type           :checkbox
            :name           :show-archived
            :on-change      #(swap! show-archived? not)
            :disabled       false
            :defaultChecked false}]
          [:label {:for :show-archived} "Show archived configurations?"]]
         [table-component/component
          {:appearance                    #{:primary}
           ::collection/items             (mapv format-queue-cells sorted-queue-configs)
           ::collection/update            #(reset! ordering (:ordering %))
           ::table-component/row-on-click #(select-queue-panel-view (:id %) false)
           ::collection/fields            (if @show-archived? all-fields active-fields)}]]))))

(defn queue-panel
  "The admin. queue tab panel. Comprises one of the following views:
    - A list of existing queue configurations.
    - A form to either enter a new queue configuration or to edit/delete an
      existing queue configuration"
  []
  (let [selected-config   @(rf/subscribe [::queue-subs/selected-queue-config])
        list-view? @(rf/subscribe [::queue-subs/list-view?])]
    [:<>
     [panel-component/panel {:appearance #{:primary}
                             :class      [:admin-page__panel]}
      (if list-view?
        [queue-list-view]
        [queue-form-view selected-config])]]))
